/**
 * usePreferencesSync (issue #424).
 *
 * Loads the authenticated user's preferences from the server (GraphQL) on
 * mount, applies them to the Redux `preferences` slice (which also applies the
 * theme to `<html data-theme>`), and — on a user's first visit, when the server
 * has no stored locale yet — detects the browser locale/timezone and persists
 * them via REST so subsequent sessions are consistent across devices.
 *
 * Intended to be mounted once near the app root (AppShell).
 */

import { useEffect, useRef } from 'react';
import { useUserPreferences } from '../graphql/queries';
import { updateUserPreferences } from '../lib/apiClient';
import { useAppDispatch, useAppSelector } from '../store';
import { selectIsAuthenticated } from '../store/features/auth/authSlice';
import {
  setPreferences,
  detectBrowserLocale,
  detectBrowserTimezone,
} from '../store/features/preferences/preferencesSlice';

export function usePreferencesSync(): void {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const detectionAttempted = useRef(false);

  const { data } = useUserPreferences({ skip: !isAuthenticated });

  useEffect(() => {
    const prefs = data?.userPreferences;
    if (!prefs) return;

    // Apply authoritative server values (theme side effect included). The
    // dashboard layout hydrates the slice too (issue #451) so it is available
    // store-wide on mount, persisting across sessions/devices.
    dispatch(
      setPreferences({
        theme: prefs.theme,
        locale: prefs.locale,
        timezone: prefs.timezone,
        ...(prefs.dashboardLayout != null && { dashboardLayout: prefs.dashboardLayout }),
      })
    );

    // First-visit locale detection: the server defaults a brand-new user to
    // en-US/UTC. If the stored values still match those defaults and we have a
    // richer browser signal, capture and persist it once.
    if (detectionAttempted.current) return;
    detectionAttempted.current = true;

    const browserLocale = detectBrowserLocale();
    const browserTimezone = detectBrowserTimezone();
    const needsLocale = prefs.locale === 'en-US' && browserLocale !== 'en-US';
    const needsTimezone = prefs.timezone === 'UTC' && browserTimezone !== 'UTC';

    if (needsLocale || needsTimezone) {
      const patch = {
        ...(needsLocale && { locale: browserLocale }),
        ...(needsTimezone && { timezone: browserTimezone }),
      };
      dispatch(setPreferences(patch));
      void updateUserPreferences(patch).catch(() => {
        // Non-fatal: detection is best-effort; the user can set it manually.
      });
    }
  }, [data, dispatch]);
}
